/**
 * 前端导入Schema配置
 * 用于纯前端Excel解析和字段映射
 */

// 费用类型枚举
export const COST_TYPE_OPTIONS = {
    'Landing/Parking': 'landing_parking',
    'Catering': 'catering',
    'Transportation': 'transportation',
    'Traffic Rights Permit': 'traffic_rights_permit',
    'Positioning': 'positioning',
    'ULD': 'uld',
    'SITA': 'sita',
    'Deicing': 'deicing',
    'Accommodation': 'accommodation',
    '地面服务': 'ground_handling',
    '燃油': 'fuel',
    '导航费': 'navigation',
    '其他费用': 'others_fee',
    '货物处理': 'cargo_handling',
    'BH': 'bh',  // Block Hours - 飞行小时
    '飞行小时': 'bh',
    'Block Hours': 'bh',
    'bh': 'bh',  // 保持小写，与数据库一致
    'FH': 'fh',  // Flight Hours - 飞行时间
    '飞行时间': 'fh',
    'Flight Hours': 'fh',
    'fh': 'fh'  // 保持小写，与数据库一致
};

// 币种输入映射：Excel 导入时把用户填写的币种（代码或中文名）归一到标准代码。
// 权威币种清单在后端 COMMON_CURRENCIES（GET /v1/exchange-rates/currencies），增删币种以后端为准，此处需同步。
export const CURRENCY_OPTIONS = {
    // 标准代码自映射（后端 COMMON_CURRENCIES 全量 14 种）
    USD: 'USD', CNY: 'CNY', EUR: 'EUR', GBP: 'GBP', JPY: 'JPY',
    HKD: 'HKD', SGD: 'SGD', AUD: 'AUD', CAD: 'CAD', CHF: 'CHF',
    CZK: 'CZK', HUF: 'HUF', KZT: 'KZT', AED: 'AED',
    // 常见中文名映射
    美元: 'USD', 人民币: 'CNY', 欧元: 'EUR', 英镑: 'GBP', 日元: 'JPY',
    港币: 'HKD', 新加坡元: 'SGD', 澳元: 'AUD', 加元: 'CAD', 瑞士法郎: 'CHF',
    捷克克朗: 'CZK', 匈牙利福林: 'HUF', 哈萨克斯坦坚戈: 'KZT',
    阿联酋迪拉姆: 'AED', 迪拉姆: 'AED',
};

// 航段费用导入Schema
export const LEG_COST_IMPORT_SCHEMA = {
    importType: "leg_cost_import",
    description: "航段费用导入",
    source_type: "excel",
    useBackendParsing: true, // 启用后端解析
    excel_config: {
        auto_detect_header: true,
        max_header_row: 5,
        skip_empty_rows: true,
    },

    // 核心字段映射配置 - 按照用户要求的顺序
    fields: [
        {
            name: "departure_airport",
            label: "始发站",
            required: true,
            mapping: [
                "始发站", "Departure Airport", "departure_airport", "始发地", "Origin", "起飞机场"
            ],
            type: "string",
            description: "起飞机场三字码"
        },
        {
            name: "arrival_airport",
            label: "目的站",
            required: true,
            mapping: [
                "目的站", "Arrival Airport", "arrival_airport", "目的地", "Destination", "到达机场"
            ],
            type: "string",
            description: "到达机场三字码"
        },
        {
            name: "flight_number",
            label: "航班号",
            required: true,
            mapping: [
                "航班号", "Flight Number", "flight_number", "航班", "Flight"
            ],
            type: "string",
            description: "航班号，如：O3761"
        },
        {
            name: "flight_date",
            label: "航班日期",
            required: true,
            mapping: [
                "航班日期", "Flight Date", "flight_date", "日期", "Date", "执行日期"
            ],
            type: "date",
            description: "航班执行日期，格式：YYYY-MM-DD"
        },
        {
            name: "timezone",
            label: "时区",
            required: false,
            mapping: [
                "时区", "Timezone", "timezone", "时间 zone"
            ],
            type: "string",
            default: "UTC",
            description: "时间区域，如：UTC"
        },
        {
            name: "cost_type",
            label: "费用类型",
            required: true,
            mapping: [
                "费用类型", "Cost Type", "cost_type", "收费项目", "收费类型", "项目"
            ],
            type: "enum",
            options: Object.keys(COST_TYPE_OPTIONS),
            description: "费用类型，如：Landing/Parking。特别说明：BH (Block Hours) 和 FH (Flight Hours) 类型不需要填写供应商、币种、汇率"
        },
        {
            name: "supplier",
            label: "供应商",
            required: true,  // 注：BH类型时此字段非必填，后端会特殊处理
            mapping: [
                "供应商", "Supplier", "服务商", "服务提供方", "vendor"
            ],
            type: "string",
            description: "服务供应商名称（BH类型可留空）"
        },
        {
            name: "currency",
            label: "币种",
            required: true,  // 注：BH类型时此字段非必填，后端会特殊处理
            mapping: [
                "币种", "Currency", "currency", "货币", "单位"
            ],
            type: "enum",
            options: Object.keys(CURRENCY_OPTIONS),
            default: "USD",
            description: "费用币种（BH类型可留空）"
        },
        {
            name: "amount",
            label: "金额",
            required: true,
            mapping: [
                "金额", "Amount", "amount", "费用金额", "费用", "price", "Price"
            ],
            type: "number",
            description: "费用金额（数字）。BH类型时表示飞行小时数，如12.55表示12小时55分钟"
        },
        {
            name: "exchange_rate",
            label: "汇率",
            required: false,
            mapping: [
                "汇率", "Exchange Rate", "exchange_rate", "rate", "兑换率"
            ],
            type: "number",
            default: 1.0,
            description: "兑换到USD的汇率（BH类型可留空）"
        }
    ]
};

// 航段费用导入模板数据
export const LEG_COST_TEMPLATE_DATA = [
    {
        "始发站": "CGO",
        "目的站": "LGG",
        "航班号": "O3761",
        "航班日期": "2024-08-01",
        "时区": "UTC",
        "费用类型": "Landing/Parking",
        "供应商": "供应商A",
        "币种": "USD",
        "金额": 1500.00,
        "汇率": 1.0
    },
    {
        "始发站": "LGG",
        "目的站": "BUD",
        "航班号": "O3762",
        "航班日期": "2024-08-02",
        "时区": "UTC",
        "费用类型": "Catering",
        "供应商": "供应商B",
        "币种": "EUR",
        "金额": 850.00,
        "汇率": 1.08
    },
    {
        "始发站": "BUD",
        "目的站": "CGO",
        "航班号": "O3763",
        "航班日期": "2024-08-03",
        "时区": "UTC",
        "费用类型": "BH",
        "供应商": "",
        "币种": "",
        "金额": 12.55,
        "汇率": ""
    }
];

// 导出配置
export default {
    LEG_COST_IMPORT_SCHEMA,
    COST_TYPE_OPTIONS,
    CURRENCY_OPTIONS,
    LEG_COST_TEMPLATE_DATA
};